import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2074&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin: 4rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #0d9488;
  text-align: center;
  font-style: italic;
`;

const CardButton = styled(motion.button)`
  background-color: #0d9488;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background-color: #0f766e;
  }
`;

const BenefitsSection = styled.div`
  margin: 6rem 0;
`;

const BenefitsTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 3rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(13, 148, 136, 0.1);
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #0d9488;
`;

const BenefitIcon = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  flex-shrink: 0;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.8rem;
  color: #0d9488;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Affiliation: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const buttonVariants = {
    hover: { 
      scale: 1.05,
      backgroundColor: '#0f766e',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
      y: -5
    },
    tap: { scale: 0.95 }
  };
  
  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      title: language === 'it' ? 'ESPANSIONE DELLA RETE PUBBLICITARIA' : 'EXPANSION OF THE ADVERTISING NETWORK',
      description: language === 'it' 
        ? 'Gli affiliati possono aiutare a espandere la rete pubblicitaria raggiungendo nuovi potenziali clienti che potrebbero non essere stati raggiunti attraverso i canali di marketing tradizionali.'
        : 'Affiliates can help expand your advertising network by reaching new potential customers who may not have been reached through traditional marketing channels.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
        </svg>
      ),
      title: language === 'it' ? 'AUMENTO DEL TRAFFICO WEB' : 'INCREASE IN WEB TRAFFIC',
      description: language === 'it' 
        ? 'Gli affiliati possono generare traffico verso il tuo sito web o le pagine di prenotazione attraverso le loro attività di marketing, portando a un aumento delle prenotazioni e dei ricavi.'
        : 'Affiliates can generate traffic to your website or booking pages through their marketing activities, leading to an increase in bookings and revenue.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
          <line x1="9" y1="9" x2="9.01" y2="9"></line>
          <line x1="15" y1="9" x2="15.01" y2="9"></line>
        </svg>
      ),
      title: language === 'it' ? 'CREDIBILITÀ E AFFIDABILITÀ' : 'CREDIBILITY AND RELIABILITY',
      description: language === 'it' 
        ? 'Collaborare con affiliati fidati e rispettati nel settore può migliorare la credibilità del tuo marchio, fornendo fiducia ai potenziali clienti.'
        : 'Partnering with trusted and respected affiliates in the industry can enhance your brand credibility, providing confidence to potential customers.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
        </svg>
      ),
      title: language === 'it' ? 'SFRUTTAMENTO DI COMPETENZE ESTERNE' : 'LEVERAGING EXTERNAL SKILLS',
      description: language === 'it' 
        ? 'Gli affiliati possono portare competenze specializzate e risorse aggiuntive, come conoscenza approfondita del mercato locale o abilità di marketing specializzate.'
        : 'Affiliates can bring specialized expertise and additional resources, such as in-depth knowledge of the local market or specialized marketing skills.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="5" x2="5" y2="19"></line>
          <circle cx="6.5" cy="6.5" r="2.5"></circle>
          <circle cx="17.5" cy="17.5" r="2.5"></circle>
        </svg>
      ),
      title: language === 'it' ? 'AUMENTO DELLE VENDITE INCROCIATE' : 'INCREASE IN CROSS-SELLING',
      description: language === 'it' 
        ? 'Gli affiliati possono promuovere offerte correlate o servizi aggiuntivi insieme alle prenotazioni di affitti a breve termine, aumentando il valore medio della transazione.'
        : 'Affiliates can promote related offers or additional services along with short-term rental bookings, increasing the average transaction value.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9"></path>
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
        </svg>
      ),
      title: language === 'it' ? 'RISULTATI MISURABILI E TRACCIABILI' : 'MEASURABLE AND TRACEABLE RESULTS',
      description: language === 'it' 
        ? 'Puoi facilmente monitorare e tracciare le prestazioni degli affiliati attraverso strumenti di analisi, permettendoti di valutare l\'efficacia delle campagne di marketing.'
        : 'You can easily monitor and track affiliate performance through analytics tools, allowing you to evaluate the effectiveness of marketing campaigns.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Affiliazione | J-O-H' : 'Affiliation | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Programma di affiliazione per il settore degli affitti brevi, un\'opportunità per espandere la tua rete e massimizzare il potenziale di guadagno' 
            : 'Affiliation program for the short-term rental sector, an opportunity to expand your network and maximize earning potential'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-owner">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>AFFILIAZIONE</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Il nostro network di affiliazione è più di una partnership: è una porta verso il successo nel mondo dell\'ospitalità. Connettiti con professionisti del settore turistico provenienti da tutto il mondo e accedi a risorse, formazione e supporto che ti aiuteranno a portare il tuo business al livello successivo.'
              : 'Our affiliate network is more than a partnership: it\'s a gateway to success in the world of hospitality. Connect with travel industry professionals from around the world and access resources, training and support that will help you take your business to the next level.'}
          </Description>
          
          <ServicesContainer>
            <ServiceCard>
              <CardImage src="https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80" alt="Network" />
              <CardContent>
                <CardTitle>NETWORK</CardTitle>
                <Link to="/services/network">
                  <CardButton
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    {language === 'it' ? 'Scopri' : 'Discover'}
                  </CardButton>
                </Link>
              </CardContent>
            </ServiceCard>
            
            <ServiceCard>
              <CardImage src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80" alt="Know How" />
              <CardContent>
                <CardTitle>KNOW HOW</CardTitle>
                <Link to="/services/know-how">
                  <CardButton
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    {language === 'it' ? 'Scopri' : 'Discover'}
                  </CardButton>
                </Link>
              </CardContent>
            </ServiceCard>
          </ServicesContainer>
          
          <BenefitsSection>
            <BenefitsTitle>
              {language === 'it' ? 'VANTAGGI DELL\'AFFILIAZIONE' : 'BENEFITS OF AFFILIATION'}
            </BenefitsTitle>
            
            <BenefitsList>
              {benefits.map((benefit, index) => (
                <BenefitItem key={index}>
                  <BenefitIcon>{benefit.icon}</BenefitIcon>
                  <BenefitContent>
                    <BenefitTitle>{benefit.title}</BenefitTitle>
                    <BenefitDescription>{benefit.description}</BenefitDescription>
                  </BenefitContent>
                </BenefitItem>
              ))}
            </BenefitsList>
          </BenefitsSection>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Unisciti al nostro network' : 'Join our network'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Affiliation; 